import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '@mui/material/Tooltip';
import { useNotificationHistory } from '../contexts/NotificationHistoryContext';
import { useOliverName } from '../contexts/OliverContext';
import './NotificationBell.css';

const IA_NOTIFICATION_ICON = '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M489-384q51 0 88.5-33t37.5-81q0-40-25-68.5T527-595q-31 0-54 20.5T450-525q0 13 5.5 25t15.5 23l23-19q-8-6-12-13t-4-15q0-15 12.5-28.5T527-566q24 0 42 18.5t18 50.5q0 34-27 59.5T490-412q-48 0-83-38t-35-96q0-29 11.5-56t33.5-49l-21-20q-25 25-39 57t-14 67q0 67 42 115t104 48ZM292-132v-144q-57-52-88.5-115.5T172-521q0-128 90-217.5T480-828q103 0 187.5 63.5T777-601l38 152q4 14-5 25.5T786-412h-78v140q0 25-17.5 42.5T648-212H548v80h-28v-108h128q14 0 23-9t9-23v-168h108l-38-155q-23-92-99-148.5T480-800q-116 0-198 81.5T200-521q0 60 24.5 113.5T294-312l26 24v156h-28Zm202-334Z"/></svg>';

function isTokenErrorMessage(message) {
  if (!message || typeof message !== 'string') return false;
  const m = message.toLowerCase();
  return /token\s*(invalide|expiré|invalid|expired)|invalide\s*ou\s*expiré|401|unauthorized/.test(m);
}

function getDisplayNotification(notification, oliverName) {
  if (!notification.pageName && isTokenErrorMessage(notification.message)) {
    return { ...notification, pageName: oliverName, title: 'Problème de token', pageIcon: IA_NOTIFICATION_ICON };
  }
  return notification;
}

/**
 * Cloche de notifications.
 * Même pattern que le bouton/panneau Historique des échanges (Oliver) :
 *   - Bouton = dashboard-header-icon-btn (même classe, même rendu)
 *   - Panneau mobile = overlay fixe inline (même CSS que bpm-ai-history-overlay)
 *   - Panneau desktop = popup relative sous le bouton
 */
const NotificationBell = ({ onPanelOpen }) => {
  const { oliverName } = useOliverName();
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [animActive, setAnimActive] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const bellRef = useRef(null);

  const { notifications, clearHistory } = useNotificationHistory();
  const [minLevel, setMinLevel] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('notification_min_level') || '3', 10);
      return [1, 2, 3].includes(v) ? v : 3;
    } catch { return 3; }
  });

  useEffect(() => {
    const h = () => {
      try {
        const v = parseInt(localStorage.getItem('notification_min_level') || '3', 10);
        setMinLevel([1, 2, 3].includes(v) ? v : 3);
      } catch { setMinLevel(3); }
    };
    window.addEventListener('notificationLevelUpdated', h);
    return () => window.removeEventListener('notificationLevelUpdated', h);
  }, []);

  // Même seuil que DashboardHeader (< 640px)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Conteneur dédié hors du header pour l'overlay mobile (évite isolation:isolate du header)
  const [overlayRoot, setOverlayRoot] = useState(null);
  useEffect(() => {
    if (!isMobile) return;
    let el = document.getElementById('notification-overlay-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'notification-overlay-root';
      document.body.appendChild(el);
    }
    setOverlayRoot(el);
  }, [isMobile]);

  // Animation d'ouverture (mobile = sync avant paint, desktop = rAF)
  useLayoutEffect(() => {
    if (!isOpen || isClosing) { setAnimActive(false); return; }
    if (isMobile) setAnimActive(true);
  }, [isOpen, isClosing, isMobile]);

  useEffect(() => {
    if (isOpen && !isClosing && !isMobile) {
      const id = requestAnimationFrame(() => setAnimActive(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen, isClosing, isMobile]);

  // Desktop : fermer au clic en dehors
  useEffect(() => {
    if (isMobile || !isOpen) return;
    const h = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        requestClose();
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [isOpen, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestClose = () => { if (!isClosing) setIsClosing(true); };

  const handleAnimEnd = (e) => {
    if (e.animationName === 'notification-dock-close') {
      setIsOpen(false);
      setIsClosing(false);
    }
  };

  const toggle = () => {
    if (!isOpen && !isClosing) {
      onPanelOpen?.();
      setIsOpen(true);
    } else if (isOpen && !isClosing) {
      requestClose();
    }
  };

  const filteredNotifications = notifications.filter(n => (n.level ?? 3) <= minLevel);
  const count = filteredNotifications.length;
  const recentNotifications = showAll ? filteredNotifications : filteredNotifications.slice(0, 10);
  const hasMore = filteredNotifications.length > 10;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const panelContent = (
    <>
      <div className="notification-popup-header">
        <h3>Notifications</h3>
        {count > 0 && (
          <Tooltip title="Effacer l'historique" placement="bottom">
            <button
              className="notification-clear-button"
              onClick={() => {
                if (window.confirm('Voulez-vous effacer tout l\'historique des notifications ?')) {
                  clearHistory();
                }
              }}
            >
              Effacer
            </button>
          </Tooltip>
        )}
      </div>
      <div className="notification-popup-content">
        {recentNotifications.length === 0 ? (
          <div className="notification-empty"><p>Aucune notification</p></div>
        ) : (
          <>
            <div className="notification-list">
              {recentNotifications.map((notification) => {
                const display = getDisplayNotification(notification, oliverName);
                const typeClass = (notification.type && ['success', 'error', 'warning', 'info'].includes(notification.type))
                  ? `notification-item--${notification.type}`
                  : 'notification-item--info';
                return (
                  <div key={notification.id} className={`notification-item ${typeClass}`}>
                    <div className="notification-item-content">
                      {display.pageName && (
                        <div className="notification-item-page">
                          {display.pageIcon && (() => {
                            let cleanIcon = display.pageIcon;
                            if (typeof cleanIcon === 'string') {
                              cleanIcon = cleanIcon.trim();
                              if (cleanIcon.startsWith('<svg') && cleanIcon.includes('</svg>')) {
                                try {
                                  const parser = new DOMParser();
                                  const doc = parser.parseFromString(cleanIcon, 'image/svg+xml');
                                  if (!doc.querySelector('parsererror')) {
                                    return <span className="notification-item-page-icon" dangerouslySetInnerHTML={{ __html: cleanIcon }} />;
                                  }
                                } catch {}
                              }
                            }
                            return null;
                          })()}
                          <span className="notification-item-page-name">{display.pageName}</span>
                        </div>
                      )}
                      {display.title && <div className="notification-item-title">{display.title}</div>}
                      <div className="notification-item-message">{display.message}</div>
                      <div className="notification-item-time">{formatTime(notification.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            {hasMore && (
              <div className={showAll ? 'notification-show-less' : 'notification-show-more'}>
                <button type="button" className={showAll ? 'notification-show-less-button' : 'notification-show-more-button'} onClick={() => setShowAll(!showAll)}>
                  {showAll ? 'Afficher moins' : 'Afficher plus'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const popupClasses = `notification-popup${animActive ? ' tooltip-dock-open' : ''}${isClosing ? ' tooltip-dock-closing' : ''}`;

  const bellSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
      <path d="M200-209.23v-40h64.62v-316.92q0-78.39 49.61-137.89 49.62-59.5 125.77-74.11V-800q0-16.67 11.64-28.33Q463.28-840 479.91-840t28.36 11.67Q520-816.67 520-800v21.85q76.15 14.61 125.77 74.11 49.61 59.5 49.61 137.89v316.92H760v40H200Zm280-286.15Zm-.14 390.76q-26.71 0-45.59-18.98-18.89-18.98-18.89-45.63h129.24q0 26.85-19.03 45.73-19.02 18.88-45.73 18.88ZM304.62-249.23h350.76v-316.92q0-72.93-51.23-124.16-51.23-51.23-124.15-51.23-72.92 0-124.15 51.23-51.23 51.23-51.23 124.16v316.92Z"/>
    </svg>
  );

  const badge = count > 0 ? (
    <span className="notification-bell-badge">{count > 99 ? '99+' : count}</span>
  ) : null;

  // Mobile : bouton dans le header, overlay portalé vers #notification-overlay-root
  // (conteneur dédié sur body, hors du stacking context isolation:isolate du header)
  if (isMobile) {
    const overlayEl = (isOpen || isClosing) ? (
      <div
        className="notification-overlay notification-overlay-portal"
        role="dialog"
        aria-label="Notifications"
        onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
      >
        <div
          className={popupClasses}
          onClick={(e) => e.stopPropagation()}
          onAnimationEnd={handleAnimEnd}
        >
          {panelContent}
        </div>
      </div>
    ) : null;

    return (
      <>
        <button
          type="button"
          className="dashboard-header-icon-btn notification-bell-mobile-btn"
          onClick={toggle}
          aria-label="Notifications"
        >
          {bellSvg}
          {badge}
        </button>
        {overlayEl && overlayRoot && createPortal(overlayEl, overlayRoot)}
      </>
    );
  }

  // Desktop : bouton dans un conteneur relatif, popup en absolute en dessous
  const buttonEl = (
    <button
      type="button"
      className="notification-bell-button"
      onClick={toggle}
      aria-label="Notifications"
      title="Notifications"
    >
      {bellSvg}
      {badge}
    </button>
  );

  return (
    <div className="notification-bell-container" ref={bellRef}>
      <Tooltip title="Notifications" placement="bottom" enterDelay={500} leaveDelay={0}>
        {buttonEl}
      </Tooltip>
      {(isOpen || isClosing) && (
        <div className={popupClasses} onAnimationEnd={handleAnimEnd}>
          {panelContent}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
